/**
 * Configuración del panel, desde las variables `VITE_*` (panel/.env.example). Solo valores públicos: la clave de
 * Reverb es pública; el secreto nunca llega al navegador.
 */
export interface ConfiguracionPanel {
    /** Origen de la API, sin `/v1` (vacío: el mismo origen que el panel). */
    api: string;
    /**
     * Datos simulados (`VITE_SIMULACION=1`): el panel usa la capa de src/simulacion/ en lugar de la API, con la forma
     * exacta del contrato. Queda como opción para revisar el diseño y para las pruebas; sin ella, la API real.
     */
    simulacion: boolean;
    /**
     * Dirección del panel (`VITE_PANEL_URL`, el `PANEL_URL` del servidor), sin barra final. La usan las páginas que se
     * sirven en otra dirección (la del pase, `pase.reservaria.app`, G821) para llevar a las del panel: «Ver mis pases»
     * va a `PANEL_URL/mis-pases` (G891). Vacía: el mismo origen.
     */
    panel: string;
    /** Dirección de la documentación de la API (`api/docs`, G642) para «Abrir la documentación» de 13.6; vacía: sin botón. */
    documentacionApi: string;
    /**
     * Entorno del panel (`VITE_ENTORNO`: `local`, `pre` o `pro`). Vacío: `local` con `vite` (desarrollo) y `pro` en una
     * construcción, para que lo que solo vale en pruebas quede apagado si nadie dice lo contrario. Lo usa «Clave de
     * pruebas» de 13.6, que no se ofrece en producción (G731).
     */
    entorno: Entorno;
    reverb: {
        clave: string;
        host: string;
        puerto: number;
        tls: boolean;
    };
}

export type Entorno = 'local' | 'pre' | 'pro';

export function leerConfiguracion(env: Record<string, string | boolean | undefined> = import.meta.env): ConfiguracionPanel {
    const texto = (nombre: string, porDefecto = ''): string => {
        const valor = env[nombre];
        return typeof valor === 'string' && valor !== '' ? valor : porDefecto;
    };

    return {
        api: texto('VITE_API_URL').replace(/\/+$/, ''),
        panel: texto('VITE_PANEL_URL').replace(/\/+$/, ''),
        documentacionApi: texto('VITE_API_DOCS_URL'),
        entorno: entornoDe(texto('VITE_ENTORNO').toLowerCase(), env.DEV === true),
        simulacion: ['1', 'true', 'si', 'sí'].includes(texto('VITE_SIMULACION').toLowerCase()),
        reverb: {
            clave: texto('VITE_REVERB_APP_KEY'),
            host: texto('VITE_REVERB_HOST', 'localhost'),
            puerto: Number(texto('VITE_REVERB_PORT', '8091')),
            tls: texto('VITE_REVERB_SCHEME', 'http') === 'https',
        },
    };
}

/** El entorno de `VITE_ENTORNO`; uno desconocido o vacío, el más prudente (`pro`) salvo en desarrollo. */
function entornoDe(valor: string, desarrollo: boolean): Entorno {
    if (valor === 'local' || valor === 'pre' || valor === 'pro') {
        return valor;
    }
    return desarrollo && valor === '' ? 'local' : 'pro';
}

/** «Clave de pruebas» (13.6) solo fuera de producción (G731); la API no cambia (`422` si se pidiera). */
export function hayClavesDePruebas(c: Pick<ConfiguracionPanel, 'entorno'>): boolean {
    return c.entorno !== 'pro';
}

/**
 * Correo de soporte de Reservaria (`VITE_SOPORTE_EMAIL`). Lo usa «Oponerme» del contrato de encargo (1.14, G931): la
 * oposición a un cambio anunciado es por escrito a soporte, sin operación. Sin valor, `soporte@reservaria.app`.
 */
export function correoDeSoporte(env: Record<string, string | boolean | undefined> = import.meta.env): string {
    const valor = env.VITE_SOPORTE_EMAIL;
    return typeof valor === 'string' && valor.trim() !== '' ? valor.trim() : 'soporte@reservaria.app';
}
